import { easedGradientStops } from "@ui/lib/gradients";
import { clsx } from "clsx";
import { CopyButton } from "./CopyButton";
import { pxToRem } from "@ui/lib/utils";

export function _Code({ raw = false, ...props }: React.ComponentPropsWithoutRef<"pre"> & { raw?: boolean }) {
  const styleProps = raw
    ? {}
    : {
        style: { ...props.style, fontVariantLigatures: "none" },
        className: clsx(
          "[&_code]:tw-font-mono [&_code]:tw-block tw-text-2xs/[1.375rem] tw-text-black",
          props.className,
        ),
      };

  return <pre {...props} {...styleProps} />;
}

type CodeBlockBorderRadius = "default" | "none";

export interface CodeBlockProps {
  children: React.ReactNode;
  code: string;
  showCopyButton?: boolean;
  "aria-label"?: string;
  borderRadius?:
    | CodeBlockBorderRadius
    | [CodeBlockBorderRadius, CodeBlockBorderRadius, CodeBlockBorderRadius, CodeBlockBorderRadius];
  scrollable?: boolean;
}

export function _CodeBlock({
  children,
  showCopyButton = true,
  "aria-label": ariaLabel,
  code,
  borderRadius = "default",
  scrollable = true,
}: CodeBlockProps) {
  const resolvedBorderRadius = Array.isArray(borderRadius)
    ? borderRadius
    : [borderRadius, borderRadius, borderRadius, borderRadius];
  const borderRadiusMap = {
    default: pxToRem(20),
    none: 0,
  } as const;

  return (
    <div
      className='tw-group/code tw-bg-white tw-flex tw-w-full tw-relative tw-overflow-hidden has-[>:focus-visible]:tw-outline-focus'
      style={{
        boxShadow: "0 0 1px rgb(0 0 0 / 0.4), 0 1px 1px rgb(0 0 0 / 0.04), 0 2px 4px rgb(0 0 0 / 0.04)",
        borderTopLeftRadius: borderRadiusMap[resolvedBorderRadius[0]],
        borderTopRightRadius: borderRadiusMap[resolvedBorderRadius[1]],
        borderBottomRightRadius: borderRadiusMap[resolvedBorderRadius[2]],
        borderBottomLeftRadius: borderRadiusMap[resolvedBorderRadius[3]],
      }}
    >
      <div
        className={clsx(
          "tw-outline-none tw-w-full tw-py-6 tw-rounded-3xl tw-flex tw-scrollbar-none",
          scrollable ? "tw-overflow-auto" : "tw-overflow-hidden",
        )}
        aria-label={ariaLabel}
      >
        <div
          className='tw-sticky tw-left-0 tw-w-6 tw-flex-none'
          style={{ background: `linear-gradient(to right, ${easedGradientStops("#ffffff", "#ffffff00")})` }}
        />
        {children}
        <div
          className={clsx("tw-sticky tw-right-0 tw-w-6 tw-flex-none", showCopyButton && "tw-ml-12")}
          style={{ background: `linear-gradient(to left, ${easedGradientStops("#ffffff", "#ffffff00")})` }}
        />
      </div>
      {showCopyButton && (
        <div className='tw-absolute tw-isolate tw-top-3 tw-right-3 [@media(hover:hover)]:tw-opacity-0 group-hover/code:tw-opacity-100 group-has-[:focus]/code:tw-opacity-100 tw-transition-opacity'>
          <div
            className='tw-absolute -tw-z-10 -tw-bottom-6 -tw-left-6 tw-size-24'
            style={{ background: "radial-gradient(50% 50% at center, #fff calc(100% - 1rem), #fff0)" }}
          />
          <CopyButton text={code} variant='secondary' />
        </div>
      )}
    </div>
  );
}
